<section *ngIf="dashboardAssignment; else legacyAssignmentEditor" class="assignment-policy assignment-policy--dashboard" aria-label="Assignment roles" data-testid="dashboard-assignment-roles" [attr.aria-busy]="referenceLoading">
  <h5 #editorHeading tabindex="-1" class="assignment-policy__sr-only">Assignment roles</h5>
  <div class="assignment-policy__role-actions" *ngIf="canAuthor && selectedPolicy?.assignmentBasisCode === 'roles'">
    <details data-assignment-actions [open]="assignmentActionsOpen()" #assignmentActions (toggle)="assignmentActionsOpen.set(assignmentActions.open)" (keydown.escape)="closeAssignmentActions($event)">
      <summary>Assignment actions</summary>
      <button uiButton [size]="undefined" severity="danger" type="button" [disabled]="assignmentControlsDisabled || formDirty()" (click)="removeSelectedPolicy()" data-testid="remove-role-assignment">Remove assignment setup</button>
    </details>
  </div>
  <p *ngIf="!canAuthor || inspectionMode" class="assignment-policy__help" role="status">You can inspect assignment settings. Author permission is required to change them.</p>
  <p *ngIf="interactionBlocked" class="assignment-policy__help" role="status">Assignment changes are temporarily unavailable while the current draft operation finishes.</p>
  <p *ngIf="stepAttachmentWarning as warning" class="assignment-policy__error" role="alert">{{ warning }}</p>
  <div *ngIf="referenceError as error" class="assignment-policy__error" role="alert">
    <strong>{{ error.title }}</strong><p *ngFor="let message of error.messages">{{ message }}</p>
    <button uiButton [size]="undefined" type="button" (click)="retryReferencesRequested.emit()">Retry role choices</button>
  </div>
  <p *ngIf="referenceLoading" role="status">Loading assignment roles… Saved selections are retained.</p>
  <div *ngIf="roleReadOnlyReason as reason" role="status"><p>{{ reason }}</p><details><summary>Retained assignment settings</summary><pre>{{ document?.assignmentPolicies | json }}</pre></details></div>
  <ng-template #roleEditError>
    <div *ngIf="localError() as error" #policyError class="assignment-policy__error" role="alert" tabindex="-1" data-assignment-error [id]="idPrefix + '-role-error'">
      <strong>{{ error }}</strong>
      <p *ngIf="formDirty()">Your last valid setup is kept until this edit is fixed.</p>
      <button *ngIf="formDirty()" uiButton [size]="undefined" [wrap]="true" type="button" [disabled]="interactionBlocked" (click)="resetPendingChanges()">Reset incomplete role edit</button>
      <small *ngIf="formDirty()">Restores the last valid assignment settings in this draft.</small>
    </div>
  </ng-template>
  <ng-container *ngIf="!stepAttachmentWarning && (!roleReadOnlyReason || formDirty())">
    <ng-container *ngIf="legacyStepPolicy as legacy; else roleSettings">
      <ui-panel panelTitle="Earlier queue assignment" [noGrow]="true"><div class="assignment-policy__role-body">
        <p><strong>Earlier queue assignment is saved.</strong> {{ codeLabel(legacy.initialQueueCode) }} · {{ assignmentModeLabel(legacy.assignmentModeCode) }}</p>
        <p>Use role assignment deliberately converts this draft's setup. It clears the initial and eligible queues, uses dashboard assignment or staff claim, and resets returned work and repeat visits to unassigned.</p>
        <p>On this step's dashboards it clears queue scope and removes queueCode from data and presentation columns. Configured presentation adds Assigned to when missing. Dashboard audience roles and other filters and defaults are retained.</p>
        <p>Active roles must validate before the conversion applies locally. Save draft persists it; published versions keep their settings.</p>
        <button uiButton [size]="undefined" type="button" [disabled]="assignmentControlsDisabled || formDirty()" (click)="convertToRoleAssignment()" data-testid="convert-role-assignment">Use role assignment</button>
      </div></ui-panel>
    </ng-container>
    <ng-template #roleSettings>
      <ui-panel panelTitle="Who can receive this work" [noGrow]="true"><div class="assignment-policy__role-body">
        <p>Choose eligible roles for this step.</p>
        <fieldset class="assignment-policy__roles" [disabled]="assignmentControlsDisabled" [attr.aria-describedby]="localError() ? idPrefix + '-role-error' : idPrefix + '-role-help'">
          <legend class="assignment-policy__sr-only">Assignment roles</legend>
          <label *ngFor="let code of visibleRoleCodes; trackBy: trackCode" class="assignment-policy__check">
            <input uiControl type="checkbox" data-role-checkbox [attr.aria-label]="'Assign work to ' + roleLabel(code)" [checked]="formValues.eligibleRoleCodes.includes(code)" (change)="toggleEligibleRole(code, $any($event.target).checked)"
              [attr.aria-invalid]="roleInputInvalid ? true : null" [attr.aria-describedby]="localError() ? idPrefix + '-role-error' : idPrefix + '-role-help'"
              [disabled]="!roleCodes.includes(code) && !formValues.eligibleRoleCodes.includes(code)" />
            <span class="assignment-policy__role-label">{{ roleLabel(code) }}<small *ngIf="!referenceLoading && !referenceError && !roleCodes.includes(code)">Unavailable saved role</small></span>
          </label>
          <p *ngIf="!referenceLoading && !referenceError && !roleCodes.length" role="status">No active roles are available. Add roles in Access Management, then retry role choices.</p>
        </fieldset>
        <button *ngIf="!referenceLoading && !referenceError && !roleCodes.length" uiButton [size]="undefined" type="button" (click)="retryReferencesRequested.emit()">Retry role choices</button>
        <p *ngIf="!formValues.eligibleRoleCodes.length && !formDirty()" class="assignment-policy__help">Choose at least one assignment role to configure who receives this work.</p>
        <ng-container *ngTemplateOutlet="roleEditError"></ng-container>
        <p [id]="idPrefix + '-role-help'" class="assignment-policy__help">Role membership, existing permissions and dashboard access still apply. These roles do not change the dashboard audience.</p>
      </div></ui-panel>
      <ui-panel panelTitle="How people receive work" [noGrow]="true"><div class="assignment-policy__role-body">
        <label><span class="assignment-policy__sr-only">How people receive work</span><select uiControl required aria-label="How people receive work" [ngModel]="formValues.assignmentModeCode" (ngModelChange)="formValues.assignmentModeCode = $event; onAssignmentModeChange()" [disabled]="assignmentControlsDisabled" [attr.aria-describedby]="localError() ? idPrefix + '-role-error' : null">
          <option value="claim_or_direct">Assign from dashboard or let staff claim work</option><option value="direct">Assign from dashboard</option><option value="claim">Staff claim work themselves</option>
          <option *ngIf="!knownChoice(formValues.assignmentModeCode, assignmentModes) || formValues.assignmentModeCode === 'queue_only'" [value]="formValues.assignmentModeCode">{{ formValues.assignmentModeCode }} (unavailable)</option>
        </select></label><p class="assignment-policy__help">First visit starts unassigned.</p>
      </div></ui-panel>
      <details class="assignment-policy__advanced assignment-policy__role-repeat">
        <summary>Returns and repeat visits <span>{{ repeatSummary }}</span></summary>
        <div class="assignment-policy__role-body">
          <label class="assignment-policy__role-field">Returned work<select uiControl aria-label="Returned work assignment" [ngModel]="formValues.returnBehaviorCode" (ngModelChange)="formValues.returnBehaviorCode = $event; onFormChange()" [disabled]="assignmentControlsDisabled">
            <option value="unassigned">Leave unassigned for a new assignment</option><option value="retain_assignment">Keep the previous person if still eligible</option><option *ngIf="formValues.returnBehaviorCode !== 'unassigned' && formValues.returnBehaviorCode !== 'retain_assignment'" [value]="formValues.returnBehaviorCode">{{ formValues.returnBehaviorCode }} (unavailable)</option>
          </select></label>
          <label class="assignment-policy__role-field">Other repeat visits<select uiControl aria-label="Repeat visit assignment" [ngModel]="formValues.reentryBehaviorCode" (ngModelChange)="formValues.reentryBehaviorCode = $event; onFormChange()" [disabled]="assignmentControlsDisabled">
            <option value="unassigned">Leave unassigned for a new assignment</option><option value="retain_assignment">Keep the previous person if still eligible</option><option *ngIf="formValues.reentryBehaviorCode !== 'unassigned' && formValues.reentryBehaviorCode !== 'retain_assignment'" [value]="formValues.reentryBehaviorCode">{{ formValues.reentryBehaviorCode }} (unavailable)</option>
          </select></label>
          <p class="assignment-policy__help">Previous person means the preceding visit of this same published step. Missing or ineligible previous assignments fall back to unassigned.</p>
        </div>
      </details>
    </ng-template>
  </ng-container>
  <ng-container *ngIf="stepAttachmentWarning || (roleReadOnlyReason && !formDirty()) || legacyStepPolicy"><ng-container *ngTemplateOutlet="roleEditError"></ng-container></ng-container>
  <small *ngIf="selectedPolicy?.assignmentBasisCode === 'roles' && !formDirty()">Changes apply to this draft. Save draft keeps them.</small>
</section>
<ng-template #legacyAssignmentEditor>
  <section
    class="assignment-policy"
    [class.assignment-policy--step]="stepMode"
    [class.assignment-policy--compact]="compactStepMode"
    [attr.aria-labelledby]="compactStepMode ? null : idPrefix + '-title'"
    [attr.aria-label]="compactStepMode ? 'Responsibility settings' : null"
    [attr.aria-busy]="referenceLoading ? 'true' : null"
    data-testid="assignment-policy"
  >
    <header *ngIf="!compactStepMode" class="assignment-policy__header">
      <div>
        <h4 [id]="idPrefix + '-title'">
          {{ stepMode ? "Responsibility" : "Assignment policies" }}
        </h4>
        <p *ngIf="!stepMode">
          {{
            allowNewPolicies
              ? "Choose eligible queues and how staff can claim or receive work."
              : "Inspect earlier assignment settings here. Configure step recipients in Designer → step → Work → Assignment."
          }}
        </p>
      </div>
      <button
        uiButton
        severity="primary"
        [outlined]="false"
        *ngIf="
          canAuthor &&
          !inspectionMode &&
          allowNewPolicies &&
          (!stepMode || (!stepPolicyRow && !isAdding()))
        "
        type="button"
        class="assignment-policy__action"
        [disabled]="!canAddPolicy || formDirty()"
        data-testid="add-assignment-policy"
        (click)="beginAddPolicy()"
      >
        {{ stepMode ? "Configure responsibility" : "Add policy" }}
      </button>
    </header>

    <p *ngIf="!stepMode" class="assignment-policy__boundary" role="note">
      Policies apply to work using the published version. Existing workflows
      keep their retained policies; permissions and current eligibility are
      checked for every assignment.
    </p>

    <p
      *ngIf="stepMode"
      class="assignment-policy__boundary"
      data-testid="step-assignment-explanation"
    >
      A team queue is a shared inbox for the staff who receive this step's work.
      Choose whether they work from that inbox, claim work, or receive an
      individual assignment. Dashboards are views of the work: several
      dashboards can show the same team queue, each filtered to a different
      status.
    </p>

    <p
      *ngIf="!inspectionMode && referenceLoading"
      class="assignment-policy__state"
      role="status"
    >
      Loading active role and queue choices. Existing policies remain available
      to inspect.
    </p>

    <div
      *ngIf="!inspectionMode && referenceError as error"
      class="assignment-policy__error"
      role="alert"
    >
      <strong>{{ error.title }}</strong>
      <ul>
        <li *ngFor="let message of error.messages">{{ message }}</li>
      </ul>
      <button
        uiButton
        [text]="true"
        [outlined]="false"
        type="button"
        class="assignment-policy__text-button"
        (click)="retryReferencesRequested.emit()"
      >
        Retry authoring choices
      </button>
    </div>

    <p
      *ngIf="statusMessage() as message"
      class="assignment-policy__notice"
      role="status"
      aria-live="polite"
    >
      {{ message }}
    </p>
    <p
      *ngIf="localError() as error"
      #policyError
      class="assignment-policy__error"
      role="alert"
      aria-live="assertive"
      tabindex="-1"
    >
      {{ error }}
      <button
        uiButton
        [text]="true"
        [outlined]="false"
        *ngIf="compactStepMode && formDirty()"
        type="button"
        class="assignment-policy__text-button"
        [disabled]="interactionBlocked"
        (click)="resetPendingChanges()"
      >
        Reset incomplete changes
      </button>
    </p>

    <ng-container *ngIf="stepMode">
      <p
        *ngIf="stepAttachmentWarning as warning"
        class="assignment-policy__error"
        role="alert"
      >
        {{ warning }}
      </p>
      <section
        *ngIf="!stepAttachmentWarning && !isEditingStep() && !isAdding()"
        class="assignment-policy__step-summary"
        data-testid="step-responsibility-summary"
      >
        <ng-container
          *ngIf="stepPolicyRow?.policy as policy; else noStepPolicy"
        >
          <div class="assignment-policy__summary-heading">
            <strong>Configured for this step</strong>
            <button
              uiButton
              [text]="true"
              [outlined]="false"
              *ngIf="
                canAuthor && !inspectionMode && canEditSelectedPolicy && !isEditingStep()
              "
              type="button"
              class="assignment-policy__text-button"
              data-testid="edit-step-responsibility"
              [disabled]="interactionBlocked || formDirty()"
              (click)="beginEditStepPolicy()"
            >
              Edit responsibility
            </button>
          </div>
          <p>
            Source: policy attached to {{ stepLabel }}. Workflow settings do not
            override it.
          </p>
          <dl class="assignment-policy__details">
            <div>
              <dt>Default team queue</dt>
              <dd>
                {{ codeLabel(policy.initialQueueCode)
                }}{{
                  queueCodes.includes(policy.initialQueueCode)
                    ? ""
                    : " (Unavailable)"
                }}
              </dd>
            </div>
            <div>
              <dt>How staff receive work</dt>
              <dd>
                {{
                  knownChoice(policy.assignmentModeCode, assignmentModes)
                    ? assignmentModeLabel(policy.assignmentModeCode)
                    : "Unsupported saved value: " + policy.assignmentModeCode
                }}
              </dd>
            </div>
            <div>
              <dt>Eligible team queues</dt>
              <dd>
                {{ savedCodes(policy.eligibleQueueCodes) }}
              </dd>
            </div>
            <div>
              <dt>Eligible roles</dt>
              <dd>
                {{ savedCodes(policy.eligibleRoleCodes) }}
              </dd>
            </div>
          </dl>
          <details class="assignment-policy__advanced">
            <summary>Assignment behavior</summary>
            <dl class="assignment-policy__details">
              <div>
                <dt>Unassigned posture</dt>
                <dd>{{ codeLabel(policy.unassignedBehaviorCode) }}</dd>
              </div>
              <div>
                <dt>Entry fallback</dt>
                <dd>{{ codeLabel(policy.fallbackBehaviorCode) }}</dd>
              </div>
              <div>
                <dt>Return behavior</dt>
                <dd>{{ codeLabel(policy.returnBehaviorCode) }}</dd>
              </div>
              <div>
                <dt>Re-entry/reset behavior</dt>
                <dd>{{ codeLabel(policy.reentryBehaviorCode) }}</dd>
              </div>
            </dl>
            <p>
              First entry starts without a person. Return and repeat visits
              follow the saved behavior and current eligibility, with the
              declared fallback when prior ownership is unavailable. A required
              queue does not require an assigned person.
            </p>
          </details>
        </ng-container>
        <ng-template #noStepPolicy>
          <strong>No responsibility policy is attached to this step.</strong>
          <p>
            Configure this step to make its assignment behavior explicit. A
            workflow policy does not provide step responsibility.
          </p>
        </ng-template>
      </section>
      <div
        *ngIf="stepReferenceWarning as warning"
        class="assignment-policy__error"
        role="alert"
      >
        <p>{{ warning }}</p>
        <button
          uiButton
          [text]="true"
          [outlined]="false"
          *ngIf="canAuthor && canRepairStepReference"
          type="button"
          class="assignment-policy__text-button"
          data-testid="repair-step-responsibility-reference"
          [disabled]="interactionBlocked || formDirty()"
          (click)="repairStepReference()"
        >
          Use attached policy
        </button>
        <small *ngIf="!stepPolicyRow"
          >Configure responsibility here to attach a policy to this step and
          update its page settings.</small
        >
      </div>
      <details
        *ngIf="!compactStepMode"
        class="assignment-policy__workflow-source"
        [open]="!stepPolicyRow && !isAdding() && !isEditingStep()"
      >
        <summary>Workflow responsibility</summary>
        <p>{{ workflowPolicySummary }}</p>
        <div class="assignment-policy__source-actions">
          <button
            uiButton
            [text]="true"
            [outlined]="false"
            *ngIf="canAuthor && workflowPolicy && !stepPolicyRow && !isAdding()"
            type="button"
            class="assignment-policy__text-button"
            data-testid="copy-workflow-responsibility"
            [disabled]="!canCopyWorkflowPolicy"
            (click)="copyWorkflowSettings()"
          >
            Copy workflow settings
          </button>
          <button
            uiButton
            [text]="true"
            [outlined]="false"
            type="button"
            class="assignment-policy__text-button"
            [disabled]="interactionBlocked"
            (click)="manageWorkflowRequested.emit()"
          >
            Manage workflow policies
          </button>
        </div>
        <small *ngIf="workflowPolicy && !stepPolicyRow"
          >Copying creates an independent policy for this step. Future
          workflow-policy changes will not update the copy.</small
        >
      </details>
    </ng-container>

    <ng-container *ngIf="!stepMode && policyCollectionView as collection">
      <div
        *ngIf="!isAdding()"
        data-assignment-collection-state
        [attr.data-presence]="collection.presence"
        [attr.data-state]="collection.state"
      >
        <p
          *ngIf="collection.presence === 'absent'"
          class="assignment-policy__empty"
        >
          No assignment collection is declared.
        </p>
        <p *ngIf="collection.presence === 'null'" class="assignment-policy__empty">
          The saved assignment collection is explicitly null.
        </p>
        <p *ngIf="collection.state === 'empty'" class="assignment-policy__empty">
          <strong>No standalone assignment policy is recorded.</strong>
          Legacy assignment behavior remains unchanged.
        </p>
        <p
          *ngIf="collection.state === 'retained-unknown'"
          class="assignment-policy__state"
          role="status"
        >
          These saved assignment settings have an unsupported shape. Their original
          value is retained.
        </p>
        <p
          *ngFor="let issue of collection.issues"
          class="assignment-policy__help"
          role="status"
        >
          {{ issue }}
        </p>
      </div>
      <details
        *ngIf="collection.presence !== 'absent'"
        class="assignment-policy__advanced assignment-policy__saved-settings"
        data-assignment-collection-raw
      >
        <summary>Original saved assignment settings</summary>
        <pre>{{ collection.raw | json }}</pre>
      </details>
    </ng-container>

    <div
      *ngIf="
        (!stepMode &&
          (policyCollectionView.rows.length > 0 || isAdding() || formDirty())) ||
        (stepMode && (isEditingStep() || isAdding()) && !stepAttachmentWarning)
      "
      class="assignment-policy__workspace"
    >
      <nav
        *ngIf="!stepMode"
        class="assignment-policy__list"
        [attr.aria-labelledby]="idPrefix + '-list-title'"
      >
        <h5 [id]="idPrefix + '-list-title'">Policy attachments</h5>
        <button
          uiButton
          [wrap]="true"
          *ngFor="let row of policyCollectionView.rows; trackBy: trackRow"
          type="button"
          [class.assignment-policy__row--selected]="
            row.index === selectedIndex()
          "
          [class.assignment-policy__row--invalid]="row.issues.length > 0"
          [attr.aria-current]="row.index === selectedIndex() ? 'true' : null"
          [attr.data-saved-assignment-index]="row.index"
          [disabled]="formDirty() || interactionBlocked"
          (click)="selectPolicy(row.selection, $event.detail === 0)"
        >
          <strong>{{ row.attachmentLabel }}</strong>
          <span>{{ row.idLabel }}</span>
          <small>{{ row.modeLabel }}</small>
          <small *ngIf="row.issues.length">
            Retained settings need review · Entry {{ row.index + 1 }}
          </small>
        </button>
      </nav>

      <section
        class="assignment-policy__inspector"
        [attr.aria-labelledby]="idPrefix + '-editor-title'"
      >
        <header [class.assignment-policy__compact-header]="compactStepMode">
          <div>
            <span *ngIf="!stepMode" class="assignment-policy__eyebrow">
              {{
                isAdding()
                  ? "New policy"
                  : inspectionMode
                    ? "Immutable policy"
                    : "Policy details"
              }}
            </span>
            <h5
              #editorHeading
              [id]="idPrefix + '-editor-title'"
              tabindex="-1"
              [class.assignment-policy__sr-only]="compactStepMode"
            >
              {{
                stepMode
                  ? "Responsibility for " + stepLabel
                  : isAdding()
                    ? "Configure assignment policy"
                    : selectedPolicyView?.idLabel || "Invalid policy entry"
              }}
            </h5>
          </div>
          <button
            uiButton
            severity="danger"
            [text]="true"
            [outlined]="false"
            *ngIf="canAuthor && !inspectionMode && selectedRow && !isAdding()"
            type="button"
            class="assignment-policy__text-button assignment-policy__remove"
            [disabled]="interactionBlocked || formDirty()"
            data-testid="remove-assignment-policy"
            (click)="removeSelectedPolicy()"
          >
            {{ compactStepMode ? "Remove responsibility" : "Remove" }}
          </button>
        </header>

        <form
          *ngIf="
            canAuthor &&
              !inspectionMode &&
              (isAdding() || canEditSelectedPolicy);
            else readOnlyPolicy
          "
          class="assignment-policy__form"
          (submit)="$event.preventDefault(); applyPolicy()"
        >
          <details
            *ngIf="!compactStepMode"
            class="assignment-policy__advanced"
            [open]="!stepMode"
          >
            <summary *ngIf="stepMode">Advanced policy identity</summary>
            <label>
              <span>Stable policy ID</span>
              <input
                uiControl
                name="policyId"
                type="text"
                [(ngModel)]="formValues.id"
                [disabled]="!isAdding() || interactionBlocked"
                autocomplete="off"
                (ngModelChange)="onFormChange()"
              />
              <small>Permanent comparison identity after publication.</small>
            </label>
          </details>

          <div class="assignment-policy__form-grid">
            <label *ngIf="!stepMode">
              <span>Attachment scope</span>
              <select
                uiControl
                name="scopeCode"
                [(ngModel)]="formValues.scopeCode"
                [disabled]="interactionBlocked"
                (ngModelChange)="onFormChange()"
              >
                <option value="workflow">Whole workflow</option>
                <option value="step">Specific work step</option>
                <option
                  *ngIf="
                    formValues.scopeCode !== 'workflow' &&
                    formValues.scopeCode !== 'step'
                  "
                  [value]="formValues.scopeCode"
                >
                  Unsupported saved value:
                  {{ formValues.scopeCode || "Missing" }}
                </option>
              </select>
            </label>
            <label *ngIf="!stepMode && formValues.scopeCode === 'step'">
              <span>Work step</span>
              <select
                uiControl
                name="nodeId"
                [(ngModel)]="formValues.nodeId"
                [disabled]="interactionBlocked"
                (ngModelChange)="onFormChange()"
              >
                <option [ngValue]="null">Choose a work step</option>
                <option *ngFor="let step of humanSteps" [ngValue]="step.id">
                  {{ step.displayName || step.stepCode || step.id }}
                </option>
              </select>
            </label>
            <label>
              <span>How staff receive work</span>
              <select
                uiControl
                name="assignmentModeCode"
                [(ngModel)]="formValues.assignmentModeCode"
                [disabled]="interactionBlocked"
                (ngModelChange)="onAssignmentModeChange()"
              >
                <option
                  *ngIf="
                    !knownChoice(formValues.assignmentModeCode, assignmentModes)
                  "
                  [value]="formValues.assignmentModeCode"
                >
                  Unsupported saved value:
                  {{ formValues.assignmentModeCode || "Missing" }}
                </option>
                <option
                  *ngFor="let option of assignmentModes"
                  [value]="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </label>
            <label>
              <span>{{
                compactStepMode ? "Team queue" : "Default team queue"
              }}</span>
              <select
                uiControl
                name="initialQueueCode"
                [attr.aria-label]="
                  compactStepMode ? 'Team queue' : 'Default team queue'
                "
                [attr.aria-describedby]="
                  compactStepMode ? null : idPrefix + '-queue-help'
                "
                [(ngModel)]="formValues.initialQueueCode"
                [disabled]="interactionBlocked"
                (ngModelChange)="onInitialQueueChange()"
              >
                <option value="">Choose a team queue</option>
                <option
                  *ngFor="let code of visibleQueueCodes; trackBy: trackCode"
                  [value]="code"
                >
                  {{ codeLabel(code)
                  }}{{ queueCodes.includes(code) ? "" : " (Unavailable)" }}
                </option>
              </select>
              <small *ngIf="!compactStepMode" [id]="idPrefix + '-queue-help'"
                >New work goes to this team queue. It is also included among the
                eligible team queues.</small
              >
            </label>
          </div>

          <label *ngIf="!compactStepMode">
            <span>Eligible team queues</span>
            <select
              uiControl
              name="eligibleQueueCodes"
              multiple
              size="4"
              [(ngModel)]="formValues.eligibleQueueCodes"
              [disabled]="interactionBlocked"
              (ngModelChange)="onFormChange()"
            >
              <option
                *ngFor="let code of visibleQueueCodes; trackBy: trackCode"
                [value]="code"
              >
                {{ codeLabel(code)
                }}{{ queueCodes.includes(code) ? "" : " (Unavailable)" }}
              </option>
            </select>
            <small>Use Ctrl/Command plus click to choose more than one.</small>
          </label>

          <label
            *ngIf="
              !compactStepMode &&
              (formValues.assignmentModeCode !== 'queue_only' ||
                formValues.eligibleRoleCodes.length > 0)
            "
          >
            <span>Eligible roles for individual assignment</span>
            <select
              uiControl
              name="eligibleRoleCodes"
              multiple
              size="4"
              [(ngModel)]="formValues.eligibleRoleCodes"
              [disabled]="interactionBlocked"
              (ngModelChange)="onFormChange()"
            >
              <option
                *ngFor="let code of visibleRoleCodes; trackBy: trackCode"
                [value]="code"
              >
                {{ codeLabel(code)
                }}{{ roleCodes.includes(code) ? "" : " (Unavailable)" }}
              </option>
            </select>
            <small
              >This is assignment eligibility, not decision eligibility.</small
            >
          </label>

          <ng-container *ngIf="compactStepMode">
            <fieldset class="assignment-policy__choices">
              <legend>Eligible team queues</legend>
              <label
                *ngFor="let code of visibleQueueCodes; trackBy: trackCode"
                class="assignment-policy__choice"
              >
                <input
                  uiControl
                  type="checkbox"
                  [checked]="formValues.eligibleQueueCodes.includes(code)"
                  [disabled]="
                    interactionBlocked ||
                    (code === formValues.initialQueueCode &&
                      formValues.eligibleQueueCodes.includes(code))
                  "
                  (change)="
                    toggleEligibleQueue(code, $any($event.target).checked)
                  "
                />
                <span
                  >{{ codeLabel(code)
                  }}{{
                    queueCodes.includes(code) ? "" : " (Unavailable)"
                  }}</span
                >
                <small *ngIf="code === formValues.initialQueueCode"
                  >Receives new work</small
                >
              </label>
            </fieldset>
            <fieldset
              *ngIf="
                formValues.assignmentModeCode !== 'queue_only' ||
                formValues.eligibleRoleCodes.length > 0
              "
              class="assignment-policy__choices"
            >
              <legend>Eligible roles</legend>
              <label
                *ngFor="let code of visibleRoleCodes; trackBy: trackCode"
                class="assignment-policy__choice"
              >
                <input
                  uiControl
                  type="checkbox"
                  [checked]="formValues.eligibleRoleCodes.includes(code)"
                  [disabled]="
                    interactionBlocked ||
                    (formValues.assignmentModeCode === 'queue_only' &&
                      !formValues.eligibleRoleCodes.includes(code))
                  "
                  (change)="
                    toggleEligibleRole(code, $any($event.target).checked)
                  "
                />
                <span
                  >{{ codeLabel(code)
                  }}{{ roleCodes.includes(code) ? "" : " (Unavailable)" }}</span
                >
              </label>
              <p *ngIf="visibleRoleCodes.length === 0">
                No active roles are available.
              </p>
            </fieldset>
          </ng-container>

          <details
            class="assignment-policy__advanced"
            [open]="!compactStepMode"
          >
            <summary *ngIf="compactStepMode">Returns and exceptions</summary>
            <button
              uiButton
              [text]="true"
              [outlined]="false"
              *ngIf="compactStepMode && workflowPolicy && !stepPolicyRow"
              type="button"
              class="assignment-policy__text-button"
              data-testid="copy-workflow-responsibility"
              [disabled]="!canCopyWorkflowPolicy"
              title="Copy into an independent policy for this step"
              (click)="copyWorkflowSettings()"
            >
              Copy workflow settings
            </button>
            <div class="assignment-policy__form-grid">
              <label>
                <span>Unassigned posture</span>
                <select
                  uiControl
                  name="unassignedBehaviorCode"
                  [(ngModel)]="formValues.unassignedBehaviorCode"
                  [disabled]="interactionBlocked"
                  (ngModelChange)="onFormChange()"
                >
                  <option
                    *ngIf="
                      !knownChoice(
                        formValues.unassignedBehaviorCode,
                        unassignedBehaviors
                      )
                    "
                    [value]="formValues.unassignedBehaviorCode"
                  >
                    Unsupported saved value:
                    {{ formValues.unassignedBehaviorCode || "Missing" }}
                  </option>
                  <option
                    *ngFor="let option of unassignedBehaviors"
                    [value]="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </label>
              <label>
                <span>When work cannot enter with eligible ownership</span>
                <select
                  uiControl
                  name="fallbackBehaviorCode"
                  [(ngModel)]="formValues.fallbackBehaviorCode"
                  [disabled]="interactionBlocked"
                  (ngModelChange)="onFormChange()"
                >
                  <option
                    *ngIf="
                      !knownChoice(
                        formValues.fallbackBehaviorCode,
                        fallbackBehaviors
                      )
                    "
                    [value]="formValues.fallbackBehaviorCode"
                  >
                    Unsupported saved value:
                    {{ formValues.fallbackBehaviorCode || "Missing" }}
                  </option>
                  <option
                    *ngFor="let option of fallbackBehaviors"
                    [value]="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
                <small *ngIf="!compactStepMode"
                  >Entry fallback may wait in the initial queue without an
                  eligible person. Invalid manual assignment actions are
                  rejected.</small
                >
              </label>
              <label>
                <span>Return behavior</span>
                <select
                  uiControl
                  name="returnBehaviorCode"
                  [(ngModel)]="formValues.returnBehaviorCode"
                  [disabled]="interactionBlocked"
                  (ngModelChange)="onFormChange()"
                >
                  <option
                    *ngIf="
                      !knownChoice(
                        formValues.returnBehaviorCode,
                        returnBehaviors
                      )
                    "
                    [value]="formValues.returnBehaviorCode"
                  >
                    Unsupported saved value:
                    {{ formValues.returnBehaviorCode || "Missing" }}
                  </option>
                  <option
                    *ngFor="let option of returnBehaviors"
                    [value]="option.value"
                    [hidden]="
                      option.value === 'retain_assignment' &&
                      formValues.scopeCode !== 'step'
                    "
                    [disabled]="
                      option.value === 'retain_assignment' &&
                      formValues.scopeCode !== 'step'
                    "
                  >
                    {{
                      option.value === "retain_assignment" &&
                      formValues.scopeCode !== "step"
                        ? "Unsupported saved value: " + option.value
                        : option.value === "retain_queue" &&
                            formValues.scopeCode !== "step"
                          ? "Clear the person and keep the current queue"
                          : option.label
                    }}
                  </option>
                </select>
              </label>
              <label>
                <span>Re-entry/reset behavior</span>
                <select
                  uiControl
                  name="reentryBehaviorCode"
                  [(ngModel)]="formValues.reentryBehaviorCode"
                  [disabled]="interactionBlocked"
                  (ngModelChange)="onFormChange()"
                >
                  <option
                    *ngIf="
                      !knownChoice(
                        formValues.reentryBehaviorCode,
                        reentryBehaviors
                      )
                    "
                    [value]="formValues.reentryBehaviorCode"
                  >
                    Unsupported saved value:
                    {{ formValues.reentryBehaviorCode || "Missing" }}
                  </option>
                  <option
                    *ngFor="let option of reentryBehaviors"
                    [value]="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </label>
            </div>
          </details>

          <details *ngIf="compactStepMode" class="assignment-policy__advanced">
            <summary>Technical identity</summary>
            <label>
              <span>Stable policy ID</span>
              <input
                uiControl
                name="policyId"
                type="text"
                [(ngModel)]="formValues.id"
                [disabled]="!isAdding() || interactionBlocked"
                autocomplete="off"
                (ngModelChange)="onFormChange()"
              />
            </label>
          </details>

          <div *ngIf="!compactStepMode" class="assignment-policy__form-actions">
            <button
              uiButton
              [text]="true"
              [outlined]="false"
              type="button"
              class="assignment-policy__text-button"
              [disabled]="!formDirty() || interactionBlocked"
              (click)="resetPendingChanges()"
            >
              Reset
            </button>
            <button
              uiButton
              severity="primary"
              [outlined]="false"
              *ngIf="!compactStepMode"
              type="submit"
              class="assignment-policy__action"
              [disabled]="
                !formDirty() || interactionBlocked || referenceLoading
              "
            >
              Apply policy
            </button>
          </div>
        </form>

        <ng-template #readOnlyPolicy>
          <p
            *ngIf="
              selectedPolicy?.assignmentBasisCode === 'roles' && !inspectionMode
            "
            class="assignment-policy__help"
          >
            Edit this step's assignment roles in Designer → step → Work →
            Assignment.
          </p>
          <ng-container *ngIf="selectedPolicyView as policy">
            <p
              *ngFor="let issue of policy.issues"
              class="assignment-policy__help"
              role="status"
            >
              {{ issue }}
            </p>
            <dl class="assignment-policy__details" data-assignment-saved-facts>
              <div
                *ngFor="let field of policy.fields"
                [attr.data-assignment-saved-field]="field.id"
                [attr.data-presence]="field.presence"
              >
                <dt>{{ field.label }}</dt>
                <dd>
                  {{ field.value
                  }}<small *ngFor="let issue of field.issues">{{ issue }}</small>
                </dd>
              </div>
            </dl>
          </ng-container>
        </ng-template>
        <details
          *ngIf="!isAdding() && selectedPolicyView as policy"
          class="assignment-policy__advanced assignment-policy__saved-settings"
          data-assignment-entry-raw
          [attr.data-saved-assignment-index]="policy.index"
        >
          <summary>Original saved policy entry {{ policy.index + 1 }}</summary>
          <pre>{{ policy.raw | json }}</pre>
        </details>
      </section>
    </div>
  </section>
</ng-template>
